import { en, enInputFormats } from './en.js';
import { ruInputFormats, ru } from './ru.js';

// Английская локаль всегда импортирована
/** Уже проваленные подгрузки (повторно не пробуем) */
const FAILED = new Set();
/** Активные подгрузки, чтобы не запускать import() повторно */
const PENDING = new Map();
/** Таблица ленивых загрузчиков */
const LOADERS = {
    ru: () => import('./ru.js').then(m => ({ locale: m.ru, formats: m.ruInputFormats })),
    ka: () => import('./ka.js').then(m => ({ locale: m.ka, formats: m.kaInputFormats })),
};
/** Есть ли локаль в реестре */
function hasLocale(lang) {
    return !!LOCALES[lang];
}
/**
 * Запустить подгрузку языка (однократно). Не бросает ошибок.
 * Если загрузка уже идёт или язык помечен как FAILED — ничего не делает.
 */
function ensureLocale(lang) {
    if (!lang || hasLocale(lang) || FAILED.has(lang) || PENDING.has(lang))
        return;
    const loader = LOADERS[lang];
    if (!loader) {
        FAILED.add(lang);
        return;
    }
    const p = loader()
        .then(({ locale, formats }) => {
        LOCALES[lang] = locale;
        FORMAT_LOCALES[lang] = formats;
    })
        .catch(() => { FAILED.add(lang); })
        .finally(() => { PENDING.delete(lang); });
    PENDING.set(lang, p);
}
/**
 * Вернёт локаль для кода lang. Если нет — пытается fallback.
 * Поведение:
 * 1) Если локаль есть — вернуть сразу
 * 2) Иначе дождаться активной загрузки (если уже идёт)
 * 3) Иначе запустить загрузку и дождаться
 * 4) Если lang не удалось — повторить для fallback
 * 5) Если и fallback не удалось — вернуть первую доступную
 * 6) Если реестр пуст — бросить ошибку
 */
async function getLocaleByCode(lang, fallback = "en") {
    // 1) Быстрый путь
    if (hasLocale(lang))
        return LOCALES[lang];
    // 2) Дождаться активной загрузки lang
    const pendingLang = PENDING.get(lang);
    if (pendingLang) {
        await pendingLang;
        if (hasLocale(lang))
            return LOCALES[lang];
    }
    else {
        // 3) Стартуем загрузку lang и ждём
        ensureLocale(lang);
        const p = PENDING.get(lang);
        if (p) {
            await p;
            if (hasLocale(lang))
                return LOCALES[lang];
        }
    }
    // 4) Повторяем ту же схему для fallback
    if (hasLocale(fallback))
        return LOCALES[fallback];
    const pendingFallback = PENDING.get(fallback);
    if (pendingFallback) {
        await pendingFallback;
        if (hasLocale(fallback))
            return LOCALES[fallback];
    }
    else {
        ensureLocale(fallback);
        const p = PENDING.get(fallback);
        if (p) {
            await p;
            if (hasLocale(fallback))
                return LOCALES[fallback];
        }
    }
    // 5) Первая доступная
    const first = Object.values(LOCALES)[0];
    if (first)
        return first;
    // 6) Совсем пусто
    throw new Error("Не удалось найти или подгрузить локали");
}
/**
* Предзагруженные локали (синхронный доступ)
* заполняются по мере подключения модулей
*/
const LOCALES = {
    en,
    ru
};
/** Параметры ввода для уже загруженных языковых ресурсов. */
const FORMAT_LOCALES = {
    en: enInputFormats,
    ru: ruInputFormats
};
/** Текущий словарь интерфейсных ресурсов. */
let Loc = en;
/** Готовые параметры ввода и сохранения данных страницы. */
let Formats = enInputFormats;
/**
 * Выбирает параметры для локального языкового контекста при его создании.
 * Без локального языка или при отсутствии его ресурса использует готовые параметры данных страницы.
 */
function getInputFormats(lang) {
    if (!lang)
        return Formats;
    return FORMAT_LOCALES[lang.toLowerCase()] ?? Formats;
}
/**
 * Подготавливает ресурсы до создания виджетов страницы.
 * @param lang Язык данных из готового контекста страницы.
 * @param uiLang Язык интерфейса из готового контекста страницы.
 */
async function ensureImSysLocale(lang, uiLang) {
    const [dataLocale, uiLocale] = await Promise.all([
        getLocaleByCode(lang),
        getLocaleByCode(uiLang)
    ]);
    const formats = FORMAT_LOCALES[dataLocale.lang] ?? enInputFormats;
    Loc = uiLocale;
    Formats = formats;
}

export { FORMAT_LOCALES, Formats, LOCALES, Loc, ensureImSysLocale, ensureLocale, getInputFormats, getLocaleByCode };
